// FWSD-5402
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Dropdown } from 'react-bootstrap'
import { toastr } from 'react-redux-toastr'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'

import CustomButton from '../../CustomButton'
import { CustomButtonToggle, CustomMenu } from '../../DropdownComponents'
import LabelTag from '../../dump/LabelTag'

import momenttz from '../../helpers/momenttz'
import { isCustomUserPermissions } from '../../helpers/helperUsers'
import { profileSettings } from '../../helpers/helperProfile'
import { isInternalUser } from '../../helpers/constants'

import './fleetConfiguration.sass'

const ProfilePreviewPanel = (props) => {
	const { t, i18n } = useTranslation('translation', { keyPrefix: 'manageVehiclesPage.fleetConfigurationContainer.profilePreviewPanel' });
	const { selected_profile, configuration, configurationdv6, configurationdv7, user, webfleetDeviceId, showActions, saveProfileComment, onEditProfile, onClickSeeHistory, openMakePreferredProfileModal, openDeleteModal, openFleetConfigurationModal, setConfiguration, setShowProgressModal } = props

	const [isEditComments, setIsEditComments] = useState(false)
	const [settingsType, setSettingsType] = useState('dv6')
	const [state, setState] = useState(null)
	const [errMessage, setErrMessage] = useState(null)

	const isDV4ConfigurationEmpty = !configuration || !Object.keys(configuration).length
	const isDV6ConfigurationEmpty = !configurationdv6 || !Object.keys(configurationdv6).length
	const isDV7ConfigurationEmpty = !configurationdv7 || !Object.keys(configurationdv7).length
	const isProd = location.hostname === 'fleet.roscolive.com';
	const DV7access = (!isProd && !['S3 buckets partner'].includes(props.company.partner)) || (isProd && ['DV7 Rosco Testing'].includes(props.company.partner));

	const getProfileContentError = () => {
		let newError = ''

		if (isDV4ConfigurationEmpty) {
			newError = newError + t('errDV4configurationEmpty')
		}

		if (isDV6ConfigurationEmpty) {
			newError = newError + t('errDV6configurationEmpty')
		}

		if (isDV7ConfigurationEmpty) {
			newError = newError + t('errDV7configurationEmpty')
		}

		setErrMessage(newError)
	}

	useEffect(() => {
		if (selected_profile) {
			setState({ ...selected_profile })
		}

		// FWSD-7558
		getProfileContentError()

		return () => {
			setIsEditComments(false)
			setSettingsType('dv6')
		}
	}, [selected_profile, configuration, configurationdv6, configurationdv7])

	if (!state || !selected_profile) {
		return (
			<div className="profile-preview-panel main-content-block no-selected-profile">
				{t('textSelectProfile')}
			</div>
		)
	}

	const { name, devices_using, created_at, last_changed_ts, preferred, comments, event_types } = state
	const userRole = user.roles[0]
	const userPermissions = user.permissions
	const isDV6 = settingsType === 'dv6'
	const isDV4 = settingsType === 'dv4'
	const isDV7 = settingsType === 'dv7'

	const currentConfig = isDV6 ? configurationdv6 : isDV7 ? configurationdv7 : configuration
	const isPreferredProfile = selected_profile && selected_profile.preferred
	const isDefaultProfile = selected_profile && selected_profile.name === 'Default'
	const isWebfleetMode = webfleetDeviceId.length > 0
	const isProfileEditAccess = (!['custom_user', 'custom_partner'].includes(userRole)
		|| isCustomUserPermissions(userRole, userPermissions, 'settings_profile_edit')
		|| isCustomUserPermissions(userRole, userPermissions, 'settings_profile_delete')
	)

	const getSettingValue = (settingArg) => {
		const { id, name, setting, type, correctValueFunction } = settingArg

		const getEventUploadType = (uploadType) => {
			switch (uploadType) {
				case 'alert_only':
					return t('alertOnly')
				case 'snapshot_only':
					return t('snapshotOnly')
				case 'video_snapshot':
					return t('videoSnapshot')
				default:
					return <span className="error-label">{t('labelNotAvailable')}</span>
			}
		}

		const ADASMinimumSpeed = () => {
			let res = currentConfig.Event.Adas.Headway.SpeedThreshold
			if (currentConfig.Event.Adas.Headway.SpeedUnit === 'mph') {
				res = Math.round((res / 1.609344) * 1e2) / 1e2
			}

			return +res.toFixed(0)
		}

		const DMSSensitivity = (settingName) => {
			switch (currentConfig.Event.Dms[settingName].Sensitivity) {
				case -1:
					return t('textLessSensitive')
				case 0:
				case '':
					return t('textDefault')
				case 1:
					return t('textMoreSensitive')
				default:
					return <span className="error-label">{t('labelNotAvailable')}</span>
			}
		}

		const getSoundValue = (setting) => {
			if ([315, 318, 321].includes(id)) {
				return currentConfig.System.Chimes.SpeedLimitViolation[setting.slice(setting.lastIndexOf('.') + 1)]
			} else if ([235, 326].includes(id)) {
				return currentConfig.System.Chimes.TrafficLightViolation[setting.slice(setting.lastIndexOf('.') + 1)]
			} else if ([242, 328].includes(id)) {
				return currentConfig.System.Chimes.StopSignViolation[setting.slice(setting.lastIndexOf('.') + 1)]
			} else if ([146, 277].includes(id)) {
				return currentConfig.System.Chimes.CameraObstruction.SoundType
			}
			return currentConfig.System.Chimes[setting.slice(setting.lastIndexOf('.') + 1)]
		}

		const getSoundType = (setting) => {
			const value = getSoundValue(setting);

			switch (value) {
				case 0:
					return t('textDisableAudio')
				case 1:
					return t('textEventVoicePrompt')
				case 2:
					return t('textEventChime')
				case 3:
					return t('textChime+VoicePrompt')
				default:
					return <span className="error-label">{t('labelNotAvailable')}</span>
			}
		}

		const getMinimumSpeed = (setting) => {
			let speed = _.get(currentConfig, setting);
			const SpeedUnit = currentConfig.System.SpeedUnit;
			if (SpeedUnit === 1) {
				speed = Math.round((speed / 1.609344) * 1e2) / 1e2;
			}

			return +speed.toFixed(0);
		}

		// const channelQuality = (channelIndex) => {

		// 	if (!_.has(currentConfig, `Channels.${channelIndex}`)) {
		// 		return <span className="error-label">{t('labelNotAvailable')}</span>
		// 	}

		// 	const { MainQuality, MainFPS } = currentConfig.Channels[channelIndex]
		// 	if (!isDV4) {
		// 		if (((channelIndex === 0 && MainQuality === 0) || (channelIndex !== 0 && MainQuality === 1)) && MainFPS === 24) {
		// 			return t('textMostStorage')
		// 		}
		// 		if (MainQuality === 3 && MainFPS === 30) {
		// 			return t('textBestVideo')
		// 		}
		// 		return t('textDefault')
		// 	}
		// 	else {
		// 		if (MainQuality === 1 && MainFPS === 7) {
		// 			return t('textMostStorage')
		// 		}
		// 		if (MainQuality === 3 && MainFPS === 30) {
		// 			return t('textBestVideo')
		// 		}
		// 		return t('textDefault')
		// 	}
		// }

		if (_.isEmpty(currentConfig)) {
			return <span className="error-label">{t('labelNotAvailable')}</span>
		}
		// if profileSettings setting key is filled then we can take his value from device configuration
		else if (setting) {
			if (!_.has(currentConfig, setting)) {
				return <span className="error-label">{t('labelNotAvailable')}</span>
			}

			const settingValue = _.get(currentConfig, setting)

			switch (id) {
				case 110: // ADAS - Forward Collision Event Audio - DV6
					return !settingValue ? t('textDisableAudio') : t('textEventChime')
				case 145: // Power Off Audio - DV6
				case 192: // Power Off Audio - DV7
				case 111: // ADAS - Headway Audio - DV6
				case 239: // ADAS - Headway Audio - DV7
				case 112: // ADAS - Tailgating Event Audio - DV6
				case 246: // ADAS - Tailgating Event Audio - DV7
				case 109: // DMS - Distraction Event Audio - DV6
				case 252: // DMS - Distraction Event Audio - DV7
				case 101: // DMS - Seatbelt Event Audio - DV6
				case 273: // DMS - Seatbelt Event Audio - DV7
				case 104: // Harsh Acceleration Event Audio - DV6
				case 287: // Harsh Acceleration Event Audio - DV7
				case 103: // Harsh Braking Event Audio - DV6
				case 292: // Harsh Braking Event Audio - DV7
				case 105: // Max Speed Event Audio - DV6
				case 303: // Max Speed Event Audio - DV7
				case 146: // Potential Ch2 Obstruction Detection Event Audio - DV6
				case 277: // Potential Ch2 Obstruction Detection Event Audio - DV7
				case 322: // G-Sensor Wake Up - DV7
				case 232: // ADAS - Front Vehicle Start Alert - DV7
				case 315: // Moderate Speed Limit Sign Detection
				case 318: // High Speed Limit Sign Detection
				case 321: // Severe Speed Limit Sign Detection
				case 235: // Traffic Light Compliance Warning Signal Approach Warning
				case 326: // Traffic Light Compliance Warning Signal Violation Warning
				case 242: // Stop Sign Compliance Warning Rolling Stop
				case 328: // Stop Sign Compliance Warning Bypass Stop
					return getSoundType(setting)
				case 324: // Stardard Blind Spot Detection Warning Minimum Speed
				case 325: // Urban Blind Spot Detection Warning Minimum Speed
				case 327: // Traffic Light Compliance Warning Minimum Speed
				case 329: // Stop Sign Compliance Warning Minimum Speed
				case 330: // Speed Limit Sign Detection Minimum Speed
					return getMinimumSpeed(setting)
			}

			if (typeof settingValue == "boolean" || type === 'boolean') {
				if (settingValue === false || settingValue === 0) {
					return t('textOff')
				}
				return t('textOn')
			}

			return (typeof correctValueFunction === 'function') ? correctValueFunction(settingValue) : settingValue
		}
		else {
			switch (id) {
				case 7: // Parking Mode - DV6
				case 196: // Parking Mode - DV7
				case 59: // Parking Mode - DV4
					return currentConfig.General.IgnitionShutdownDelay === 0 ? t('textOff') : t('textOn')
				case 9: // Potential Ch2 Obstruction Detection Voice Prompt
				case 275: // Potential Ch2 Obstruction Detection Voice Prompt - DV7
					switch (currentConfig.System.Chimes.CameraObstruction.Enabled) {
						case 0:
							return t('textNoPrompt')
						case 1:
							return t('textOnePrompt')
						case 2:
							return t('textContinuousPrompt')
						default:
							return <span className="error-label">{t('labelNotAvailable')}</span>
					}
				case 114: // Potential Ch2 Obstruction Upload Type
					return getEventUploadType(event_types.dv6_camera_block)
				case 276: // Potential Ch2 Obstruction Upload Type - DV7
				case 199: // Display Timezone in Video - DV7
					return getEventUploadType(event_types.dv7_camera_block)
				case 10: // Speed Unit - DV6
				case 197: // Speed Unit - DV7
				case 60: // Speed Unit - DV4
					return currentConfig.System.SpeedUnit === 2 ? 'KPH' : 'MPH'

				case 26: // ADAS - Minimum Speed
				case 247: // ADAS - Minimum Speed - DV7
					return ADASMinimumSpeed()

				case 93: // DMS - Distraction Sensitivity
				case 251: // DMS - Distraction Sensitivity - DV7
					return DMSSensitivity('Distraction')
				case 32: // DMS - Yawning Sensitivity
				case 255: // DMS - Yawning Sensitivity - DV7
					return DMSSensitivity('Yawning')
				case 35: // DMS - Dozing Sensitivity
				case 259: // DMS - Dozing Sensitivity - DV7
					return DMSSensitivity('Dozing')
				case 38: // DMS - Phone Use Sensitivity
				case 263: // DMS - Phone Use Sensitivity - DV7
					return DMSSensitivity('PhoneUse')
				case 96: // DMS - Smoking Sensitivity
				case 265: // DMS - Smoking Sensitivity - DV7
					return DMSSensitivity('Smoking')
				case 100: // DMS - Seatbelt Sensitivity
				case 272: // DMS - Seatbelt Sensitivity - DV7
					return DMSSensitivity('Seatbelt')

				case 52: // Max Speed Threshold
				case 302: // Max Speed Threshold - DV7
				case 92: // Max Speed Threshold
					return currentConfig.System.SpeedUnit === 2 ? currentConfig.Event.HighSpeed.Threshold : (currentConfig.Event.HighSpeed.Threshold / 1.609344).toFixed(0)

				// case 13: // Channel 1 Recording
				// case 63: // Channel 1 Recording
				// 	return channelQuality(0)
				// case 16: // Channel 2 Recording
				// case 66: // Channel 2 Recording
				// 	return channelQuality(1)
				// case 19: // Channel 3 Recording
				// case 69: // Channel 3 Recording
				// 	return channelQuality(2)
				// case 72: // Channel 3 Recording
				// 	return channelQuality(3)

				case 21: // ADAS - Forward Collision Upload Type - DV6
					return getEventUploadType(event_types.dv6_forward_collision)
				case 23: // ADAS - Headway Monitoring Upload Type
					return getEventUploadType(event_types.dv6_headway_monitoring)
				case 238: // ADAS - Headway Monitoring Upload Type - DV7
					return getEventUploadType(event_types.dv7_headway_monitoring)
				case 25: // ADAS - Tailgating Upload Type
					return getEventUploadType(event_types.dv6_following_distance)
				case 245: // ADAS - Tailgating Upload Type - DV7
					return getEventUploadType(event_types.dv7_following_distance)
				case 29: // DMS - Distraction Upload Type
					return getEventUploadType(event_types.dv6_distraction)
				case 250: // DMS - Distraction Upload Type - DV7
					return getEventUploadType(event_types.dv7_distraction)
				case 31: // DMS - Yawning Upload Type
					return getEventUploadType(event_types.dv6_yawning)
				case 254: // DMS - Yawning Upload Type - DV7
					return getEventUploadType(event_types.dv7_yawning)
				case 34: // DMS - Dozing Upload Type
					return getEventUploadType(event_types.dv6_drowsiness)
				case 258: // DMS - Dozing Upload Type - DV7
					return getEventUploadType(event_types.dv7_drowsiness)
				case 37: // DMS - Phone Use Upload Type
					return getEventUploadType(event_types.dv6_phone_use)
				case 262: // DMS - Phone Use Upload Type - DV7
					return getEventUploadType(event_types.dv7_phone_use)
				case 40: // Driver Event Upload Type
					return getEventUploadType(event_types.dv6_driver)
				case 279: // Driver Event Upload Type - DV7
					return getEventUploadType(event_types.dv7_driver)
				case 42: // Harsh Acceleration Upload Type
					return getEventUploadType(event_types.dv6_harsh_accel)
				case 284: // Harsh Acceleration Upload Type - DV7
					return getEventUploadType(event_types.dv7_harsh_accel)
				case 43: // Harsh Acceleration Sensitivity
				case 286: // Harsh Acceleration Sensitivity - DV7
					return Math.abs(currentConfig.Event.HarshAccel.Threshold * 0.28 / 9.81).toFixed(2)
				case 45: // Harsh Braking Upload Type
					return getEventUploadType(event_types.dv6_harsh_braking)
				case 289: // Harsh Braking Upload Type - DV7
					return getEventUploadType(event_types.dv7_harsh_braking)
				case 46: // Harsh Braking Sensitivity

					return Math.abs(currentConfig.Event.HarshBraking.Threshold * 0.28 / 9.81).toFixed(2)
				case 48: // High G-Force Upload Type
					return getEventUploadType(event_types.dv6_fatal_gsensor)
				case 294: // High G-Force Upload Type - DV7
					return getEventUploadType(event_types.dv7_fatal_gsensor)
				case 51: // Max Speed Upload Type
					return getEventUploadType(event_types.dv6_high_speed)
				case 301: // Max Speed Upload Type - DV7
					return getEventUploadType(event_types.dv7_high_speed)
				case 95: // DMS - Smoking Upload Type
					return getEventUploadType(event_types.dv6_smoking)
				case 266: // DMS - Smoking Upload Type - DV7
					return getEventUploadType(event_types.dv7_smoking)
				case 99: // DMS - Seatbelt Upload Type
					return getEventUploadType(event_types.dv6_seatbelt)
				case 271: // DMS - Seatbelt Upload Type - DV7
					return getEventUploadType(event_types.dv7_seatbelt)

				case 78: // DMS - Distraction Upload Type - DV4
					return getEventUploadType(event_types.dv4_distraction)
				case 80: // DMS - Drowsiness/Looking Down Upload Type
					return getEventUploadType(event_types.dv4_drowsiness)
				case 82: // DMS - Phone Use/Hand to Head Upload Type
					return getEventUploadType(event_types.dv4_phone_use)
				case 84: // Driver Event Upload Type
					return getEventUploadType(event_types.dv4_driver)
				case 86: // External Driver Event Upload Type
					return getEventUploadType(event_types.dv4_ext_driver)
				case 88: // High G-Force Upload Type
					return getEventUploadType(event_types.dv4_fatal_gsensor)
				case 91: // Max Speed Upload Type
					return getEventUploadType(event_types.dv4_high_speed)
				case 298: // G-Sensor Wake-Up Upload Type
					return getEventUploadType(event_types.dv7_gsensor_wakeup)
				case 281: // Dangerous Backing-Up Upload Type
					return getEventUploadType(event_types.dv7_backup_warning)
				case 231: // ADAS - Front Vehicle Start Alert Upload Type
					return getEventUploadType(event_types.dv7_front_vehicle_start)
				case 234: // ADAS - Traffic Light Compliance Warning Upload Type
					return getEventUploadType(event_types.dv7_traffic_light_violation)
				case 241: // ADAS - Stop Sign Compliance Warning Upload Type
					return getEventUploadType(event_types.dv7_stop_sign_violation)
				case 305: // Standard Blind Spot Detection Warning
					return getEventUploadType(event_types.dv7_blind_spot_warning_standard)
				case 308: // Urban Blind Spot Detection Warning
					return getEventUploadType(event_types.dv7_blind_spot_warning_urban)
				case 311: // Low Speed Limit Sign Detection
					return getEventUploadType(event_types.dv7_speed_limit_violation_low)
				case 314: // Moderate Speed Limit Sign Detection
					return getEventUploadType(event_types.dv7_speed_limit_violation_moderate)
				case 317: // High Speed Limit Sign Detection
					return getEventUploadType(event_types.dv7_speed_limit_violation_high)
				case 320: // Severe Speed Limit Sign Detection
					return getEventUploadType(event_types.dv7_speed_limit_violation_severe)
			}
		}

		return <span className="error-label">{t('labelNotAvailable')}</span>
	}

	const getSettingPrefix = (settingArg) => {
		const { id, name, setting } = settingArg

		if (_.isEmpty(currentConfig)) {
			return ''
		}

		switch (id) {
			case 6: // Parking Duration - DV6
			case 194: // Parking Duration - DV7
			case 58: // Parking Mode - DV4
				return ' min'
			case 4: // Event Chime Volume
			case 191: // Event Chime Volume - DV7
				return '%'
			case 26: // ADAS - Minimum Speed
				return ' ' + currentConfig.Event.Adas.Headway.SpeedUnit.toUpperCase()
			case 27: // ADAS - Following Distance Time
			case 248: // ADAS - Following Distance Time - DV7
				return t('textSeconds')
			case 43: // Harsh Acceleration Sensitivity
			case 286: // Harsh Acceleration Sensitivity - DV7
			case 46: // Harsh Braking Sensitivity
			case 49: // High G-Force Sensitivity
			case 295: // High G-Force Sensitivity - DV7
			case 89: // High G-Force Sensitivity
				return ' Gs'
			case 52: // Max Speed Threshold
			case 92: // Max Speed Threshold
				return currentConfig.System.SpeedUnit === 2 ? ' KPH' : ' MPH'
		}
	}

	// const getSettingBeginning = (settingArg) => {
	// 	const { id, name, setting } = settingArg

	// 	switch (id) {
	// 		case 43: // Harsh Acceleration Sensitivity
	// 			return '+'
	// 		case 46: // Harsh Braking Sensitivity
	// 			return '-'
	// 	}
	// }

	const onPushToVehicleClick = () => {
		const isDV4ConfigurationEmpty = !configuration || !Object.keys(configuration).length
		const isDV6ConfigurationEmpty = !configurationdv6 || !Object.keys(configurationdv6).length
		const isDV7ConfigurationEmpty = !configurationdv7 || !Object.keys(configurationdv7).length

		if (isDV4ConfigurationEmpty) {
			toastr.error(t('errDV4configurationEmpty'))
			return true
		}

		if (isDV6ConfigurationEmpty) {
			toastr.error(t('errDV6configurationEmpty'))
			return true
		}

		if (isDV7ConfigurationEmpty) {
			toastr.error(t('errDV7configurationEmpty'))
			return true
		}

		openFleetConfigurationModal();
		setConfiguration({ isPushing: true })
	}

	const optsComments = {}
	if (!isProfileEditAccess || !isEditComments) {
		optsComments.readOnly = 'readOnly'
	}

	const updateProgressClick = () => {
		setShowProgressModal(true);
	}

	return (
		<div className="profile-preview-panel main-content-block">
			<div className="panel-header mb-20">
				<div className="name-wrapper">
					<div className={`profile-name${errMessage.length ? ' has-error' : ''}`}>{name}</div>
					{preferred &&
						<CustomButton
							size="icon"
							variant="primary"
							prefix="no-cursor mr-20"
						>
							{t('btnPreferred')}
						</CustomButton>
					}
				</div>

				{/* Actions dropdown menu */}
				{showActions && (
					<div className='subheader-section'>
						<Dropdown
							className='actions-dropdown'
							role="menuitem"
						>
							<Dropdown.Toggle as={CustomButtonToggle} variant="primary" prefix="actions dropdown-toggle btn btn-default">
								{t('btnActions')}
								<span className="caret" />
							</Dropdown.Toggle>
							<Dropdown.Menu as={CustomMenu}>
								{/* Edit */}
								{!isDefaultProfile && isProfileEditAccess && <Dropdown.Item as="button" onClick={onEditProfile}>{t('btnEdit')}</Dropdown.Item>}
								<Dropdown.Item as="button" onClick={updateProgressClick}>{t('btnUpdateProgress')}</Dropdown.Item>
								{/* Make Preferred Profile */}
								{!isPreferredProfile && isProfileEditAccess &&
									<Dropdown.Item as="button" onClick={openMakePreferredProfileModal}>{t('btnMakePreferredProfile')}</Dropdown.Item>
								}
								{/* Push to Vehicles */}
								{!isWebfleetMode && isCustomUserPermissions(userRole, userPermissions, 'settings_profile_view')
									&& (
										<Dropdown.Item
											as="button"
											onClick={onPushToVehicleClick}
										>{t('btnPushVehicles')}</Dropdown.Item>
									)}
								{/* See Change History						 */}
								{!isDefaultProfile && ['system_admin', 'customer_service', 'partner'].includes(userRole)
									&& <Dropdown.Item as="button" onClick={onClickSeeHistory}>{t('btnSeeChangeHistory')}</Dropdown.Item>
								}
								{/* Delete */}
								{!isDefaultProfile && isCustomUserPermissions(userRole, userPermissions, 'settings_profile_delete') &&
									<>
										<Dropdown.Divider />
										<Dropdown.Item as="button" bsPrefix="dropdown-item delete" onClick={openDeleteModal}>{t('btnDelete')}</Dropdown.Item>
									</>
								}
							</Dropdown.Menu>
						</Dropdown>
					</div>
				)}
			</div>

			{errMessage &&
				<div className="error-message">{errMessage}</div>
			}

			<main className="edit-asset-block">
				{/** INFO */}
				<section className="info-section mb-20">
					<h3 className="category-title">
						<span className="mr-30">{t('titleInfo')}</span>
					</h3>
					<div className="info-category">
						<div className="device-param">
							<strong>{t('textProfileApplied')}</strong> {devices_using} {t('textCameras')}
						</div>
						<div className="device-param">
							<strong>{t('textCreatedOn')}</strong> {momenttz(created_at).format('MM/DD/YYYY h:mm:ss A') || t('labelNotAvailable')}
						</div>
						<div className="device-param">
							<strong>{t('textLastUpdated')}</strong> {momenttz(last_changed_ts).format('MM/DD/YYYY h:mm:ss A') || t('labelNotAvailable')}
						</div>
					</div>
				</section>

				{/** COMMENTS */}
				<section className="info-section mb-20 comments">
					<h3 className="category-title">
						<span className="mr-30">{t('titleComments')}</span>
						{isProfileEditAccess
							? isEditComments
								? <span>
									<CustomButton
										variant="link"
										prefix="red"
										onClick={() => {
											// setGlobalThreshold(defaultGlobalThreshold);
											setState((prevState) => ({ ...prevState, comments: selected_profile.comments }))
											setIsEditComments(false)
										}}
									>
										{t('btnCancel')}
									</CustomButton>
									<CustomButton
										variant="link"
										onClick={() => {
											saveProfileComment(comments)
											setIsEditComments(false)
										}}
									>
										{t('btnSave')}
									</CustomButton>
								</span>
								: <CustomButton variant="link" onClick={() => setIsEditComments(true)}>{t('btnEditComments')}</CustomButton>
							: ''
						}
					</h3>
					<div className="info-category">
						<textarea
							className={`${(isProfileEditAccess && isEditComments) && 'editable'}`}
							placeholder={t('placeholderNoComments')}
							value={comments || ''}
							onChange={(e) => setState((prevState) => ({ ...prevState, comments: e.target.value }))}
							{...optsComments}
						/>
					</div>
				</section>

				{/** PROFILE SETTINGS */}
				<section className="info-section mb-20">
					<h3 className="category-title">
						<span className="mr-30">{t('titleProfileSettings')}</span>
					</h3>
					<div className="toggle-wrapper button-group mb-20">
						<div className="toggle-buttons">
							{DV7access &&
								<CustomButton
									variant='toggle'
									active={isDV7}
									value="dv7"
									onClick={() => setSettingsType('dv7')}
								>
									DV7
								</CustomButton>}
							<CustomButton
								variant='toggle'
								active={isDV6}
								value="dv6"
								onClick={() => setSettingsType('dv6')}
							>
								DV6
							</CustomButton>
							<CustomButton
								variant='toggle'
								active={isDV4}
								value="dv4"
								onClick={() => setSettingsType('dv4')}
							>
								DV4
							</CustomButton>
						</div>
					</div>
					{// (settingsType === 'dv4' && !isDV4ConfigurationEmpty || settingsType === 'dv6' && !isDV6ConfigurationEmpty)
						// && Object.keys(profileSettings[settingsType]).map((category) => {
						Object.keys(profileSettings[settingsType]).map((category) => {
							return (
								<div key={category} className="info-category">
									<h4 className="info-title">{t(`${category}`).toUpperCase()}</h4>
									{
										profileSettings[settingsType][category].map((setting) => {
											if (setting.isInternal && !isInternalUser(userRole)) {
												return <div key={setting.id} />
											}
											else {
												return (
													<div key={setting.id} className="device-param">
														<strong>
															{i18n.t(`manageVehiclesPage.fleetConfigurationContainer.profilePreviewPanel.profileSettings.${settingsType}.${setting.name.replace(/\s/g, "_")}`)}
															{setting.depreciated ? <span className="depreciated">{t('textDepreciated')}</span> : ''}:
														</strong> {getSettingValue(setting)}
														{getSettingPrefix(setting)}
														{setting.isInternal && <LabelTag text={t('textInternal')} tag_type='ml-10 internal' />}
													</div>
												)
											}
										})
									}
								</div>
							)
						})
					}
				</section>
			</main>
		</div>
	)
}

ProfilePreviewPanel.propTypes = {
	selected_profile: PropTypes.oneOfType([PropTypes.objectOf(PropTypes.any), PropTypes.string]),
	configuration: PropTypes.objectOf(PropTypes.any),
	configurationdv6: PropTypes.objectOf(PropTypes.any),
	saveProfileComment: PropTypes.func,
	onEditProfile: PropTypes.func,
	onClickSeeHistory: PropTypes.func,
	openMakePreferredProfileModal: PropTypes.func,
	openDeleteModal: PropTypes.func,
	openFleetConfigurationModal: PropTypes.func,
	setConfiguration: PropTypes.func,
}

export default ProfilePreviewPanel;
